import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Plus, Trash2, EyeOff, Eye, Save } from "lucide-react";
import "./AdminFoundingCircle.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const dollars = (c) => ((c || 0) / 100).toString();

export default function AdminFoundingCircle() {
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | denied | ready
  const [data, setData] = useState(null);
  const [camp, setCamp] = useState(null);
  const [add, setAdd] = useState({ name: "", amount: "", message: "", tier: "", anonymous: false });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/founding-circle`);
      setData(r.data);
      setCamp({
        ...r.data.campaign,
        goal_d: dollars(r.data.campaign.goal_cents),
        offset_d: dollars(r.data.campaign.manual_offset_cents),
      });
      setState("ready");
    } catch (e) {
      setState(e.response?.status === 403 ? "denied" : "denied");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCampaign = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/founding-circle/campaign`, {
        title: camp.title, tagline: camp.tagline, blurb: camp.blurb,
        goal_cents: Math.round(parseFloat(camp.goal_d || "0") * 100),
        manual_offset_cents: Math.round(parseFloat(camp.offset_d || "0") * 100),
        tiers: camp.tiers,
      });
      load();
    } catch (e) { alert("Could not save campaign."); }
    setSaving(false);
  };

  const addContribution = async () => {
    if (!add.name || !add.amount) { alert("Name and amount required."); return; }
    try {
      await axios.post(`${API}/admin/founding-circle/contribution`, {
        name: add.name, amount_cents: Math.round(parseFloat(add.amount) * 100),
        message: add.message, tier: add.tier, anonymous: add.anonymous,
      });
      setAdd({ name: "", amount: "", message: "", tier: "", anonymous: false }); load();
    } catch (e) { alert("Could not add supporter."); }
  };

  const toggleHide = async (c) => { await axios.patch(`${API}/admin/founding-circle/contribution/${c.id}`, { hidden: !c.hidden }); load(); };
  const del = async (c) => { if (window.confirm("Delete this supporter record?")) { await axios.delete(`${API}/admin/founding-circle/contribution/${c.id}`); load(); } };

  const updateTier = (i, key, val) => {
    const tiers = camp.tiers.map((t, idx) => idx === i ? { ...t, [key]: key === "amount_cents" ? Math.round(parseFloat(val || "0") * 100) : val } : t);
    setCamp({ ...camp, tiers });
  };

  if (state === "loading") return <div className="afc-loading"><Loader2 className="spin" /></div>;
  if (state === "denied") return (
    <div className="afc-denied" data-testid="afc-denied">
      <h1>Admin access only</h1>
      <p>This page is restricted to the platform owner.</p>
      <button onClick={() => navigate("/dashboard")}>Back to Dashboard</button>
    </div>
  );

  const t = data.totals;
  return (
    <div className="afc" data-testid="admin-founding-circle">
      <header className="afc-top">
        <span>Founding Circle · Admin</span>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button onClick={() => navigate("/admin/musical-intelligence")}
                  data-testid="afc-mi-preview-link">Musical Intelligence™ · Preview</button>
          <button onClick={() => navigate("/admin/musical-intelligence/canary")}
                  data-testid="afc-mi-canary-link">Musical Intelligence™ · Canary Validation</button>
          <button onClick={() => navigate("/admin/billing")} data-testid="afc-billing-link">Billing Admin →</button>
          <button onClick={() => navigate("/dashboard")}>← Dashboard</button>
        </div>
      </header>

      <div className="afc-body">
        <div className="afc-stats" data-testid="afc-stats">
          <div className="afc-stat"><b data-testid="afc-raised">{usd(t.raised_cents)}</b><span>Raised</span></div>
          <div className="afc-stat"><b>{usd(t.goal_cents)}</b><span>Goal</span></div>
          <div className="afc-stat"><b>{t.supporters}</b><span>Paid Supporters</span></div>
          <div className="afc-stat"><b>{usd(camp.manual_offset_cents)}</b><span>Offline Offset</span></div>
        </div>

        <section className="afc-card">
          <h2>Campaign Settings</h2>
          <label>Title</label>
          <input value={camp.title} onChange={(e) => setCamp({ ...camp, title: e.target.value })} data-testid="afc-title" />
          <label>Tagline</label>
          <input value={camp.tagline || ""} onChange={(e) => setCamp({ ...camp, tagline: e.target.value })} />
          <label>Blurb</label>
          <textarea value={camp.blurb || ""} onChange={(e) => setCamp({ ...camp, blurb: e.target.value })} />
          <div className="afc-2col">
            <div><label>Goal ($)</label><input type="number" value={camp.goal_d} onChange={(e) => setCamp({ ...camp, goal_d: e.target.value })} data-testid="afc-goal" /></div>
            <div><label>Offline / manual offset ($)</label><input type="number" value={camp.offset_d} onChange={(e) => setCamp({ ...camp, offset_d: e.target.value })} data-testid="afc-offset" /></div>
          </div>

          <label>Tiers</label>
          {(camp.tiers || []).map((tier, i) => (
            <div className="afc-tier-row" key={i}>
              <input value={tier.label} onChange={(e) => updateTier(i, "label", e.target.value)} placeholder="Label" />
              <input type="number" value={dollars(tier.amount_cents)} onChange={(e) => updateTier(i, "amount_cents", e.target.value)} placeholder="$" />
              <input value={tier.perk || ""} onChange={(e) => updateTier(i, "perk", e.target.value)} placeholder="Perk" />
            </div>
          ))}
          <button className="afc-save" onClick={saveCampaign} disabled={saving} data-testid="afc-save-campaign">
            {saving ? <Loader2 size={15} className="spin" /> : <Save size={15} />} Save Campaign
          </button>
        </section>

        <section className="afc-card">
          <h2>Add Supporter (offline / manual)</h2>
          <div className="afc-add">
            <input placeholder="Name" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} data-testid="afc-add-name" />
            <input placeholder="Amount $" type="number" value={add.amount} onChange={(e) => setAdd({ ...add, amount: e.target.value })} data-testid="afc-add-amount" />
            <input placeholder="Tier" value={add.tier} onChange={(e) => setAdd({ ...add, tier: e.target.value })} />
            <input placeholder="Message (optional)" value={add.message} onChange={(e) => setAdd({ ...add, message: e.target.value })} />
            <label className="afc-anon"><input type="checkbox" checked={add.anonymous} onChange={(e) => setAdd({ ...add, anonymous: e.target.checked })} /> Anon</label>
            <button onClick={addContribution} data-testid="afc-add-btn"><Plus size={15} /> Add</button>
          </div>
        </section>

        <section className="afc-card">
          <h2>Supporters ({data.contributions.length})</h2>
          <div className="afc-table" data-testid="afc-contributions">
            <div className="afc-tr afc-th"><span>Name</span><span>Amount</span><span>Tier</span><span>Status</span><span>Source</span><span>Actions</span></div>
            {data.contributions.map((c) => (
              <div className={`afc-tr${c.hidden ? " hidden" : ""}`} key={c.id} data-testid={`afc-row-${c.id}`}>
                <span>{c.anonymous ? "Anonymous" : (c.display_name || "—")}</span>
                <span>{usd(c.amount_cents)}</span>
                <span>{c.tier || "—"}</span>
                <span className={`afc-badge ${c.status}`}>{c.status}</span>
                <span>{c.source}</span>
                <span className="afc-actions">
                  <button onClick={() => toggleHide(c)} title={c.hidden ? "Show" : "Hide"} data-testid={`afc-hide-${c.id}`}>{c.hidden ? <Eye size={15} /> : <EyeOff size={15} />}</button>
                  <button onClick={() => del(c)} title="Delete" data-testid={`afc-del-${c.id}`}><Trash2 size={15} /></button>
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
